import { SsaFunction, isVariable } from './SsaTypes';

/** The WASM local index behind an SSA variable name (localN / paramN / phi_N), or null. */
export function localBackedIndex(baseName: string): number | null {
  const direct = baseName.match(/^(?:local|param)(\d+)/);
  if (direct) {
    return parseInt(direct[1], 10);
  }
  const phiMatch = baseName.match(/^phi_(\d+)/);
  return phiMatch ? parseInt(phiMatch[1], 10) : null;
}

/** One independent live range of a WASM local: the SSA versions that flow together. */
export interface LiveRange {
  localIndex: number;
  variableIds: Set<number>;
}

/**
 * Splits each local's SSA versions into independent live ranges — versions linked
 * through a phi node belong to the same range. A WASM local slot the compiler
 * reused for two unrelated values (e.g. a counter, then a pointer) yields two
 * separate ranges, each of which can be named on its own. Must run before
 * out-of-SSA phi elimination, while phi nodes still connect the versions.
 */
export function computeLocalLiveRanges(ssaFunc: SsaFunction): LiveRange[] {
  const parent = new Map<number, number>();
  const localIndexById = new Map<number, number>();
  const find = (id: number): number => {
    let root = id;
    while (parent.get(root) !== root) {
      root = parent.get(root)!;
    }
    while (parent.get(id) !== root) {
      const next = parent.get(id)!;
      parent.set(id, root);
      id = next;
    }
    return root;
  };
  const union = (left: number, right: number): void => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) {
      parent.set(leftRoot, rightRoot);
    }
  };

  for (const variable of ssaFunc.variables) {
    const localIndex = localBackedIndex(variable.name);
    if (localIndex !== null && !parent.has(variable.id)) {
      parent.set(variable.id, variable.id);
      localIndexById.set(variable.id, localIndex);
    }
  }
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind !== 'phi') {
        continue;
      }
      const localIndex = localBackedIndex(instr.result.name);
      if (localIndex === null || !parent.has(instr.result.id)) {
        continue;
      }
      for (const input of instr.inputs) {
        if (isVariable(input.value) && parent.has(input.value.id) && localBackedIndex(input.value.name) === localIndex) {
          union(instr.result.id, input.value.id);
        }
      }
    }
  }

  return groupRangesByRoot(parent, find, localIndexById);
}

// Collects the union-find members into one LiveRange per root component.
function groupRangesByRoot(
  parent: Map<number, number>,
  find: (id: number) => number,
  localIndexById: Map<number, number>,
): LiveRange[] {
  const rangesByRoot = new Map<number, LiveRange>();
  for (const id of parent.keys()) {
    const root = find(id);
    let range = rangesByRoot.get(root);
    if (!range) {
      range = { localIndex: localIndexById.get(root)!, variableIds: new Set() };
      rangesByRoot.set(root, range);
    }
    range.variableIds.add(id);
  }
  return [...rangesByRoot.values()];
}
